import { observer } from "mobx-react";
import { MTextInput } from "@/components-v2/TextInput";
import { Box, Container, VBox } from "@/components-v2/Box";
import { useTranslation } from "@/stores/RootStoreContext";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import {
  companyRolesCompanies,
  companyRolesCountryOfResidence,
  companyRolesEmail,
  companyRolesNationality,
  companyRolesRoleType,
  companyRolesValidFrom,
  CompanyRolesViewProps,
} from "./companyRolesDomain";
import { AggregatedContactItem } from "./contactsDomain";
import { TextWithTooltip } from "@/components-v2/TextWithTooltip";
import { Icon } from "@/components-v2/Icon.tsx";
import { LastLogin } from "@/common/commonComponents.tsx";
import { DropdownMenu } from "@/components-v2/Dropdown.tsx";
import { IconButton } from "@/components-v2/Button.tsx";
import { getRoleTypeLabel } from "@/types/enums/RoleType.ts";

export const CompanyRolesView = observer((props: CompanyRolesViewProps) => {
  const { t } = useTranslation();
  const noWrap = () => "whitespace-nowrap";
  const columns: TableColumn<AggregatedContactItem>[] = [
    {
      name: "name",
      label: t("name"),
      align: "left",
      getValue: (row) => (
        <Box alignItems={"center"} gap={"md"}>
          <Icon size={"sm"} color={"subtle"} name={"user"} />
          <VBox gap={false}>
            <div>{row.name}</div>
            <div className="subtle text-sm">
              <TextWithTooltip {...companyRolesRoleType(row.roleItems)} />
            </div>
          </VBox>
        </Box>
      ),
      getSortValue: (row) => row.name ?? t(companyRolesRoleType(row.roleItems).text),
      getFilterValue: (row) =>
        [
          row.name,
          ...(row.roleItems.length > 1
            ? row.roleItems.map((r) => t(getRoleTypeLabel(r.role.role)))
            : [t(companyRolesRoleType(row.roleItems).text)]),
        ].join(" "),
    },
    {
      name: "companies",
      label: t("portfolioContacts.companies"),
      align: "left",
      getValue: (row) => <TextWithTooltip {...companyRolesCompanies(row.roleItems)} />,
      getSortValue: (row) => t(companyRolesCompanies(row.roleItems).text),
      getFilterValue: (row) => row.roleItems.map((c) => c.company.companyName).join(" "),
    },
    {
      name: "validFrom",
      getClassName: noWrap,
      label: { key: "company-roles.period.from" },
      align: "left",
      getValue: (row, _, { lang }) => (
        <TextWithTooltip {...companyRolesValidFrom(row.roleItems, lang)} />
      ),
    },
    {
      name: "nationality",
      label: { key: "shareholders.detailed.nationality" },
      align: "left",
      getValue: (row) => <TextWithTooltip {...companyRolesNationality(row.roleItems)} />,
      getSortValue: (row) => t(companyRolesNationality(row.roleItems).text),
      getFilterValue: (row) =>
        [
          ...row.roleItems.map((n) => n.role.nationality),
          t(companyRolesNationality(row.roleItems).text),
        ].join(" "),
    },
    {
      name: "countryOfResidence",
      label: { key: "contacts.company-roles.country" },
      align: "left",
      getValue: (row) => <TextWithTooltip {...companyRolesCountryOfResidence(row.roleItems)} />,
      getSortValue: (row) => t(companyRolesCountryOfResidence(row.roleItems).text),
      getFilterValue: (row) =>
        [
          ...row.roleItems.map((n) => n.role.residentialAddress?.country),
          t(companyRolesCountryOfResidence(row.roleItems).text),
        ].join(" "),
    },
    {
      name: "email",
      label: t("email"),
      align: "left",
      getValue: (row) => <TextWithTooltip {...companyRolesEmail(row.roleItems)} />,
      getSortValue: (row) => t(companyRolesEmail(row.roleItems).text),
      getFilterValue: (row) =>
        [...row.roleItems.map((n) => n.role.email), t(companyRolesEmail(row.roleItems).text)].join(
          " ",
        ),
    },
    {
      name: "lastLogin",
      label: { key: "last-login" },
      align: "left",
      getValue: (row) => (
        <LastLogin
          lastLogin={row.roleItems.find((f) => f.role.lastLogin)?.role.lastLogin}
          neverLabel={{ key: "never-logged-in.short" }}
        />
      ),
    },
  ];
  return (
    <Container padding maxWidth="xl">
      <Box wrap className={"self-start"} alignItems={"center"} marginBottom={"md"}>
        {props.roleNameFilter && (
          <MTextInput
            model={props.roleNameFilter}
            icon={"magnifying-glass"}
            placeholder={t("portfolioContacts.nameFilterPlaceholder")}
            type={"search"}
            className={"basis-32 max-w-60"}
          />
        )}
        {props.filterMenus.map(({ name, menu }) => (
          <DropdownMenu key={name} {...menu} />
        ))}
        {props.resetFilters && (
          <IconButton
            icon={"arrow-counter-clockwise"}
            tooltip={t("reset")}
            onClick={props.resetFilters}
          />
        )}
      </Box>
      <DataTable
        variant={"LARGE"}
        tableColumns={columns}
        filter={props.roleNameFilter?.inputValue}
        tableData={props.aggregatedPersonItems}
        getId={(row) => row.aggregatedContactId}
        initialSortBy={"name"}
        initialSortDirection={"asc"}
        onRowClick={(row) => props.navigateToDetails(row.aggregatedContactId)}
        rowActions={props.rowActions}
      />
    </Container>
  );
});
